/**
 * Procedure player: the step list with the current step, the description as
 * sanitised HTML, the caution banner, training images with a lightbox, and
 * previous, play or pause, replay, next and stop. State lives in
 * useTwin.procedure; the choreography runs in useProcedureRunner.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Image as ImageIcon,
  Pause,
  Play,
  RotateCcw,
  SkipBack,
  SkipForward,
  Square,
  TriangleAlert,
} from 'lucide-react';
import { Chip, IconButton } from '@/components/ui/primitives';
import { cn } from '@/lib/cn';
import { findSubsystem, useTwin } from '../../store';
import { Button, SectionTitle } from '../components/layout';
import { htmlToText, sanitizeHtml } from '../lib/sanitizeHtml';
import { Lightbox } from './Lightbox';

function stepTitle(description: string, title: string | undefined, index: number): string {
  if (title) return title;
  const text = htmlToText(description);
  const m = /Step\s*\d+\s*[:-]\s*([^.]+?)(?:\s+Title:|$|\.)/i.exec(text);
  return m ? m[1].trim() : `Step ${index + 1}`;
}

export function ProcedurePlayer() {
  const state = useTwin((s) => s.procedure);
  const element = useTwin((s) => s.element);
  const setStep = useTwin((s) => s.setProcedureStep);
  const setPlaying = useTwin((s) => s.setProcedurePlaying);
  const stop = useTwin((s) => s.stopProcedure);
  const [lightbox, setLightbox] = useState<{ step: string; index: number } | null>(null);
  const playerRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (descriptionRef.current) descriptionRef.current.scrollTop = 0;
    playerRef.current?.scrollIntoView({ block: 'start', behavior: 'instant' });
  }, [state?.procedure.id, state?.stepIndex, state?.revision]);

  const titles = useMemo(
    () => (state?.procedure.steps ?? []).map((s, i) => stepTitle(s.description, s.title, i)),
    [state?.procedure.steps],
  );
  if (!state) return null;
  const { procedure, stepIndex, playing } = state;
  const stepKey = `${procedure.id}:${stepIndex}:${state.revision ?? 0}`;
  const step = procedure.steps[stepIndex];
  const total = procedure.steps.length;
  const html = sanitizeHtml(step?.description ?? '');
  const training = step?.training ?? [];

  return (
    <div
      className="flex flex-col gap-3 rounded-control border border-accent/40 bg-raised p-3"
      data-testid="procedure-player"
      ref={playerRef}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-ink-3">
            {procedure.type === 'timeline' ? 'Maintenance procedure' : 'Procedure'}
          </div>
          <div className="truncate font-display text-sm font-semibold text-ink">{procedure.name}</div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Chip tone={playing ? 'live' : 'neutral'} pulse={playing}>
            {playing ? 'Playing' : 'Paused'}
          </Chip>
          <IconButton label="Stop procedure (Esc)" onClick={stop}>
            <Square className="h-3.5 w-3.5" />
          </IconButton>
        </div>
      </div>

      {procedure.initialDescription && (
        <details className="text-[11px] leading-relaxed text-ink-2">
          <summary className="cursor-pointer text-ink">Procedure overview and source context</summary>
          <p className="mt-2">{htmlToText(procedure.initialDescription)}</p>
        </details>
      )}

      <ol
        className="flex max-h-40 flex-col gap-0.5 overflow-y-auto rounded-control border border-hairline p-1 scrollbar-thin"
        aria-label="Steps"
      >
        {procedure.steps.map((s, i) => (
          <li key={i}>
            <button
              type="button"
              onClick={() => setStep(i)}
              aria-current={i === stepIndex ? 'step' : undefined}
              className={cn(
                'flex w-full items-center gap-2 rounded-control px-2 py-1 text-left text-[11px] transition hover:bg-overlay',
                i === stepIndex ? 'bg-accent-soft text-ink' : i < stepIndex ? 'text-ink-3' : 'text-ink-2',
              )}
            >
              <span
                className={cn(
                  'w-5 shrink-0 font-mono text-[10px]',
                  i === stepIndex ? 'text-accent' : 'text-ink-3',
                )}
              >
                {i + 1}
              </span>
              <span className="min-w-0 flex-1 truncate">{titles[i]}</span>
              {s.training && s.training.length > 0 && (
                <ImageIcon className="h-3 w-3 shrink-0 text-ink-3" aria-label="Has training images" />
              )}
            </button>
          </li>
        ))}
      </ol>

      <div className="flex items-center justify-between">
        <SectionTitle className="mb-0">
          Step {stepIndex + 1} of {total}
        </SectionTitle>
        {step?.highlight && step.highlight.length > 0 && (
          <span
            className="truncate text-[10px] text-ink-3"
            title={step.highlight
              .map((id) => findSubsystem(element?.subsystems ?? [], id)?.label ?? id.replaceAll('_', ' '))
              .join(', ')}
          >
            {step.highlight
              .slice(0, 3)
              .map((id) => findSubsystem(element?.subsystems ?? [], id)?.label ?? id.replaceAll('_', ' '))
              .join(', ')}
            {step.highlight.length > 3 ? ` +${step.highlight.length - 3}` : ''}
          </span>
        )}
      </div>
      {step?.viewNote && (
        <p
          className="rounded-control border border-hairline bg-inset px-3 py-2 text-[11px] leading-relaxed text-ink-2"
          data-testid="procedure-view-note"
        >
          {step.viewNote}
        </p>
      )}
      <div
        className="twin-prose max-h-[320px] overflow-y-auto rounded-control border border-hairline bg-inset/40 px-3 py-2 text-xs leading-relaxed text-ink-2 scrollbar-thin"
        dangerouslySetInnerHTML={{ __html: html }}
        ref={descriptionRef}
      />

      {step?.warning && step.warning.trim() !== '' && (
        <div className="flex items-start gap-2 rounded-control border border-caution/40 bg-caution/10 px-3 py-2 text-xs text-ink">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-caution" />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-caution">Special cautions</div>
            <div className="leading-snug">{step.warning.replace(/^[^\p{L}\p{N}]+/u, '')}</div>
          </div>
        </div>
      )}

      {training.length > 0 && (
        <div>
          <SectionTitle right={<span className="font-mono text-[10px] text-ink-3">{training.length}</span>}>
            Training images
          </SectionTitle>
          <div className="grid grid-cols-3 gap-1.5">
            {training.map((t, i) => (
              <button
                key={`${t.image}-${i}`}
                type="button"
                onClick={() => {
                  setPlaying(false);
                  setLightbox({ step: stepKey, index: i });
                }}
                className="group overflow-hidden rounded-control border border-hairline bg-inset text-left hover:border-accent/60"
                title={t.title || t.description}
              >
                <img
                  src={t.image}
                  alt={t.title || `Training image ${i + 1}`}
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover transition group-hover:scale-[1.03]"
                />
                {t.title && <div className="truncate px-1.5 py-1 text-[10px] text-ink-2">{t.title}</div>}
              </button>
            ))}
          </div>
          <Lightbox
            images={training}
            index={lightbox?.step === stepKey ? lightbox.index : null}
            onIndex={(index) => setLightbox({ step: stepKey, index })}
            onClose={() => setLightbox(null)}
          />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1">
        <Button onClick={() => setStep(stepIndex - 1)} disabled={stepIndex === 0} title="Previous step">
          <SkipBack className="h-3.5 w-3.5" /> Previous
        </Button>
        <Button onClick={() => setPlaying(!playing)} primary title={playing ? 'Pause' : 'Play with dwell'}>
          {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
          {playing ? 'Pause' : 'Play'}
        </Button>
        <Button onClick={() => setStep(stepIndex)} title="Replay this step's choreography">
          <RotateCcw className="h-3.5 w-3.5" /> Replay
        </Button>
        <Button onClick={() => setStep(stepIndex + 1)} disabled={stepIndex >= total - 1} title="Next step">
          Next <SkipForward className="h-3.5 w-3.5" />
        </Button>
        <span className="ml-auto font-mono text-[10px] text-ink-3">
          dwell {Math.round((step?.dwellMs ?? 6000) / 1000)} s
        </span>
      </div>
    </div>
  );
}
